import React, { Dispatch, SetStateAction, useState } from 'react';
import {
  View, Text, StyleSheet, FlatList, ActivityIndicator,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../../../../constants/Colors';
import Style from '../../../../../constants/Style';
import AreaItem from './AreaItem';
import NoAreasImage from '../../NoAreasImage';
import { Area, Floor } from '../../../../../components/Map/SetFloorsModal/FloorItem';
import ScreenEmptyState from '../../../../../components/ScreenEmptyState';
import { UserPermission } from '../../../../../constants/Types';

interface AreaListProps {
  areas: Area[],
  parentName: string,
  setSelectedAreaId: Dispatch<SetStateAction<string>>,
  removeArea,
  updateArea,
  showScreenAlert,
  selectedFloor: Floor,
  navigateToAddArea: () => void,
}

const AreaList = ({
  areas, parentName, navigateToAddArea, setSelectedAreaId, removeArea, showScreenAlert, updateArea,
  selectedFloor,
}: AreaListProps) => {
  const { isAdmin, permissions }: { isAdmin: boolean, permissions: UserPermission[] } = useSelector(
    (state: { user: { isAdmin: boolean, permissions: UserPermission[] } }) => state.user,
  );
  const canAddLocations = isAdmin || permissions?.includes(UserPermission.LOCATIONS_CREATE);
  const happyEnabled: boolean = useSelector((state: { happyEnabled: { enabled: boolean } }) => state
    .happyEnabled.enabled);
  const canUpdateLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_UPDATE);
  const canDeleteLocations: boolean = isAdmin
    || permissions.includes(UserPermission.LOCATIONS_DELETE);
  const { id }: { id: string, name: string } = useSelector((state:
    { selectedBuilding: { id: string, name: string } }) => state.selectedBuilding);
  const actionsShown: boolean = canUpdateLocations || canDeleteLocations
    || id === selectedFloor.LocationId;
  const [loading, setLoading] = useState<boolean>(false);

  return <View
    style={styles.container}
    testID='area-list'
  >
    <View style={[Style.row, styles.header]}>
      <Text style={[styles.parameterTitle, styles.titleSection]}>{'AREAS'}</Text>
      <Text style={[styles.parameterTitle, styles.prioritySection]}>{'PRIORITY'}</Text>
      <Text style={[styles.parameterTitle, styles.planSection]}>{'SPACES'}</Text>
      <Text style={[styles.parameterTitle, styles.planSection]}>{'ASSETS'}</Text>
      {happyEnabled
        && <Text style={[styles.parameterTitle, styles.planSection]}>{'SENSORS & METERS'}</Text>}
      {actionsShown
        && <Text style={[styles.parameterTitle, styles.actionSection]}>{'ACTIONS'}</Text>}
    </View>
    <FlatList
      data={Object.values(areas ?? {}).filter((area: Area | null) => !!area)
        .sort((a: Area, b: Area) => a.Name?.localeCompare(b.Name))}
      renderItem={({ item: area, index }) => <AreaItem
        {...{
          parentName,
          setSelectedAreaId,
          removeArea,
          showScreenAlert,
          setLoading,
          updateArea,
          isAdmin,
          selectedFloor,
          area,
        }}
        testID={`area#${index + 1}`}
      />}
      style={styles.list}
      ListEmptyComponent={<ScreenEmptyState
        image={<NoAreasImage />}
        title='No areas in this floor'
        subtitle='Areas and spaces are created on the map'
        adminSubtitle={'Areas and spaces are created on the map.\nAdd an area on the map to have it appear in this list'}
        buttonTitle='Go to map'
        buttonAction={navigateToAddArea}
        actionAvailable={canAddLocations}
      />}
      keyExtractor={(area: Area) => `area_${area.LocationId}`}
    />
    <ActivityIndicator
      animating={loading}
      size='large'
      style={Style.activityIndicator}
    />
  </View>;
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.light.hereworksWhite500,
    paddingHorizontal: 16,
    marginBottom: 8,
    marginHorizontal: 24,
  },
  parameterTitle: {
    fontSize: 10,
    lineHeight: 15,
    fontFamily: 'Roboto_500Medium',
    color: Colors.light.hereworksBlack400,
    flex: 1,
    alignSelf: 'flex-start',
    textAlign: 'left',
  },
  titleSection: { flex: 0.36 },
  prioritySection: { flex: 0.13 },
  planSection: { flex: 0.1 },
  actionSection: {
    flex: 0.21,
    alignSelf: 'flex-end',
    textAlign: 'right',
  },
  list: {
    paddingHorizontal: 24,
    flex: 1,
  },
});

export default AreaList;
